import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  XCircle,
  Rocket,
  Users,
  Briefcase,
  Clock,
  FileText,
  UserPlus,
  Star,
  Zap,
  Building2,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  TrendingUp,
  Search,
  Lock,
  Headphones,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const PRO_ADDONS = [
  {
    label: '1 Additional Seat',
    price: '$99.99',
    badge: null,
    tagline: null,
    description: null,
    highlights: [],
  },
  {
    label: '15 Job Posts',
    price: '$199.99',
    badge: 'Starter',
    tagline: 'Best for small teams or occasional hiring',
    description:
      'Post up to 15 jobs with full access to our hiring network. Ideal for startups and growing teams with light hiring needs. Quickly publish roles, reach qualified candidates, and manage your hiring pipeline efficiently — all in one place.',
    highlights: [
      'Up to 15 active job postings',
      'Access to qualified candidate pool',
      'Seamless posting and management',
      'Works with Free and Pro plans',
    ],
  },
  {
    label: '35 Job Posts',
    price: '$399.99',
    badge: 'Best Value',
    tagline: 'Best value for scaling teams',
    description:
      'Designed for teams actively hiring across multiple roles. Post up to 35 jobs and streamline your recruiting with better reach, faster turnaround, and centralized candidate management.',
    highlights: [
      'Up to 35 job postings',
      'Optimized for multi-role hiring',
      'Increased hiring velocity',
      'Cost-effective for growing companies',
    ],
  },
  {
    label: '65 Job Posts',
    price: '$699.99',
    badge: 'High Volume',
    tagline: 'Best for high-volume hiring',
    description:
      'Built for organizations with continuous or large-scale hiring needs. Post up to 65 jobs and maximize your visibility across the talent network while managing everything from a single dashboard.',
    highlights: [
      'Up to 65 job postings',
      'Ideal for ongoing or bulk hiring',
      'Maximum reach and efficiency',
      'Scales with your recruiting demand',
    ],
  },
];

interface PlanFeature {
  icon: React.ElementType;
  title: string;
  desc: string;
  free: boolean;
  pro: boolean;
  freeNote?: string;
  proNote?: string;
}

const FEATURE_MATRIX: PlanFeature[] = [
  {
    icon: Briefcase,
    title: 'Job Posting',
    desc: 'Post Workday positions and project requirements to our consultant network.',
    free: true,
    pro: true,
    freeNote: 'Via Perplexity team',
    proNote: 'Self-service, full access',
  },
  {
    icon: Search,
    title: 'Browse Workday Talent',
    desc: 'Access verified Workday consultants and SMEs across all modules and specializations.',
    free: true,
    pro: true,
  },
  {
    icon: MessageSquare,
    title: 'Interview Request & Scheduling',
    desc: 'Submit interview requests and coordinate scheduling with consultant candidates.',
    free: true,
    pro: true,
    freeNote: 'Via Perplexity team',
    proNote: 'Self-service, full access',
  },
  {
    icon: Zap,
    title: 'Hiring & Onboarding',
    desc: 'Full onboarding for temp contractors and direct hires — messaging, background checks, drug screening, and verification.',
    free: true,
    pro: true,
    freeNote: 'Via Perplexity (temp contractors & direct hires)',
    proNote: 'Via Perplexity (temp contractors & direct hires)',
  },
  {
    icon: Users,
    title: 'Candidate Contact',
    desc: 'Connect directly with candidates during the hiring process.',
    free: true,
    pro: true,
    freeNote: 'Provided by Perplexity during scheduling',
    proNote: 'Self-service, direct access',
  },
  {
    icon: Star,
    title: 'Pro Job Postings (Boosted)',
    desc: 'Reveal your company name and details on postings for maximum candidate engagement.',
    free: false,
    pro: true,
    proNote: 'Per-seat toggle',
  },
  {
    icon: TrendingUp,
    title: 'Candidate Pipeline & Tracking',
    desc: 'Manage your full candidate funnel — track stages, add notes, and move candidates through your process.',
    free: false,
    pro: true,
  },
  {
    icon: MessageSquare,
    title: 'Candidate Messaging (Direct)',
    desc: 'Send and receive direct messages with consultants inside the platform.',
    free: false,
    pro: true,
  },
  {
    icon: Clock,
    title: 'Timesheet Approvals',
    desc: 'Workday contractor placement and direct hire through Perplexity — full timesheet submission and approval management included.',
    free: true,
    pro: true,
    freeNote: 'Via Perplexity team',
    proNote: 'Via Perplexity team',
  },
  {
    icon: FileText,
    title: 'Document Management',
    desc: 'Securely share documents, onboarding materials, and resources with your consultants.',
    free: true,
    pro: true,
  },
  {
    icon: Headphones,
    title: 'Priority Support',
    desc: 'Direct access to the Perplexity team for fast, dedicated assistance.',
    free: true,
    pro: true,
  },
  {
    icon: UserPlus,
    title: 'Team Seats',
    desc: 'Invite team members to collaborate inside the platform.',
    free: true,
    pro: true,
    freeNote: 'Unlimited seats',
    proNote: 'Unlimited + additional seats at $99/seat',
  },
];

export default function Pricing() {
  const navigate = useNavigate();
  const [expandedAddon, setExpandedAddon] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background text-text relative">
      <div className="bubble" />
      <div className="bubble" />
      <div className="bubble" />
      <Navbar />

      <div className="max-w-6xl mx-auto px-4 pt-32 pb-24">
        {/* Page Header */}
        <div className="text-center mb-16">
          <p className="text-sm font-semibold tracking-widest text-primary uppercase mb-3">
            PerplexityATS
          </p>
          <h1 className="text-4xl sm:text-5xl font-bold mb-5 text-glow">
            Simple, Transparent Pricing
          </h1>
          <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto">
            Choose the plan that fits your hiring needs. Start for free and upgrade
            as your team grows.
          </p>
        </div>

        {/* Pricing Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start mb-16">

          {/* ——— Card 1: Pro Plan ——— */}
          <div className="relative card-gradient rounded-2xl p-8 sm:p-10 border-2 border-primary/60 glow-effect flex flex-col">
            {/* Popular badge */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <span className="bg-primary text-black text-xs font-bold px-5 py-1.5 rounded-full tracking-wide uppercase shadow-lg">
                Most Popular
              </span>
            </div>

            {/* Header */}
            <div className="text-center mb-8 pt-2">
              <div className="inline-flex p-4 rounded-full bg-primary/20 glow-effect mb-4">
                <Building2 className="w-10 h-10 text-primary" />
              </div>
              <p className="text-xs font-semibold tracking-widest text-primary uppercase mb-1">
                PerplexityATS
              </p>
              <h2 className="text-3xl font-bold gradient-text mb-2">Pro Plan</h2>
              <p className="text-gray-400 text-sm mb-6">
                Self-service hiring power — run your full talent pipeline independently.
              </p>

              {/* Base price */}
              <div className="bg-black/30 rounded-xl p-5 border border-primary/20 mb-2">
                <p className="text-gray-400 text-sm mb-1">Starting at</p>
                <div className="flex items-end justify-center gap-2">
                  <span className="text-5xl font-bold text-white">$499</span>
                  <span className="text-primary font-semibold text-lg mb-1">.99</span>
                </div>
                <p className="text-gray-400 text-sm mt-1">per month</p>
              </div>
            </div>

            {/* What's included */}
            <div className="space-y-3 mb-8">
              <p className="text-xs font-semibold tracking-widest text-primary uppercase">
                Included in base price
              </p>
              {[
                '3 Starting Seats',
                'Full PerplexityATS Dashboard Access',
                'Self-Service Job Posting & Management',
                'Full Access to Workday Talent Pool',
                'Candidate Pipeline & Tracking',
                'Direct Candidate Messaging',
                'Interview Request Management (Self-Service)',
                'Pro Job Postings — Company Name Revealed',
                'Hiring & Onboarding via Perplexity',
                'Timesheet Approvals via Perplexity',
                'Document Management',
                'Priority Perplexity Team Support',
              ].map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-gray-200 text-sm">{item}</span>
                </div>
              ))}
            </div>

            {/* Add-ons */}
            <div className="mb-8">
              <p className="text-xs font-semibold tracking-widest text-primary uppercase mb-3">
                Optional Add-ons
              </p>
              <div className="space-y-2">
                {PRO_ADDONS.map((addon, i) => (
                  <div key={i} className="rounded-lg border border-primary/20 overflow-hidden">
                    <button
                      onClick={() => setExpandedAddon(expandedAddon === i ? null : i)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-black/30 hover:bg-black/50 transition-all group"
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-gray-200 text-sm font-medium">{addon.label}</span>
                        {addon.badge && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/20 text-primary border border-primary/30 leading-none">
                            {addon.badge}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        <span className="text-primary font-bold text-sm">{addon.price}</span>
                        {addon.description ? (
                          expandedAddon === i ? (
                            <ChevronUp className="w-4 h-4 text-gray-400 group-hover:text-primary transition-colors" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-primary transition-colors" />
                          )
                        ) : null}
                      </div>
                    </button>

                    {addon.description && expandedAddon === i && (
                      <div className="px-4 pb-4 pt-3 bg-black/40 border-t border-primary/10">
                        {addon.tagline && (
                          <p className="text-primary text-xs font-semibold uppercase tracking-wide mb-2">
                            {addon.tagline}
                          </p>
                        )}
                        <p className="text-gray-300 text-xs leading-relaxed mb-3">
                          {addon.description}
                        </p>
                        {addon.highlights.length > 0 && (
                          <div className="space-y-1.5">
                            <p className="text-gray-500 text-[10px] font-semibold uppercase tracking-widest mb-1">
                              Highlights
                            </p>
                            {addon.highlights.map((h) => (
                              <div key={h} className="flex items-start gap-2">
                                <CheckCircle className="w-3.5 h-3.5 text-primary flex-shrink-0 mt-0.5" />
                                <span className="text-gray-300 text-xs">{h}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                <p className="text-gray-500 text-[11px] pt-1 leading-relaxed">
                  Additional seating can be added after purchase in Subscription &amp; Billing.
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-auto">
              <button
                onClick={() => navigate('/customer-signup')}
                className="w-full gradient-border bg-primary/15 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-primary/25 transition-all transform hover:scale-105 glow-effect flex items-center justify-center gap-3"
              >
                Get Started with Pro
                <Rocket className="w-5 h-5" />
              </button>
              <p className="text-center text-gray-500 text-xs mt-3">
                No long-term contracts. Cancel anytime.
              </p>
            </div>
          </div>

          {/* ——— Card 2: Free Plan ——— */}
          <div className="card-gradient rounded-2xl p-8 sm:p-10 border-2 border-white/10 hover:border-primary/40 transition-all flex flex-col">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex p-4 rounded-full bg-white/5 mb-4">
                <Users className="w-10 h-10 text-gray-300" />
              </div>
              <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-1">
                PerplexityATS
              </p>
              <h2 className="text-3xl font-bold text-white mb-2">Free Plan</h2>
              <p className="text-gray-400 text-sm mb-6">
                Full-service hiring support — Perplexity handles the heavy lifting.
                Contracting fees apply.
              </p>

              {/* Price display */}
              <div className="bg-black/30 rounded-xl p-5 border border-white/10 mb-2">
                <div className="flex items-end justify-center gap-1">
                  <span className="text-5xl font-bold text-white">$0</span>
                </div>
                <p className="text-gray-400 text-sm mt-1">Interactive job placement through the Perplexity Team</p>
                <p className="text-yellow-400/80 text-xs mt-2 font-medium">Contracting fees apply on placement</p>
              </div>
            </div>

            {/* Features */}
            <div className="space-y-4 mb-8 flex-1">
              <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">
                Everything included
              </p>
              {[
                { icon: Briefcase, title: 'Job Posting', desc: 'Via the Perplexity team — submit requirements, we handle posting and routing to the right consultants.' },
                { icon: Search, title: 'Browse Workday Talent', desc: 'Access our full database of verified Workday consultants across all modules and specializations.' },
                { icon: MessageSquare, title: 'Interview Request & Scheduling', desc: 'Perplexity coordinates scheduling, confirms availability, and manages all communication.' },
                { icon: Zap, title: 'Hiring & Onboarding', desc: 'Perplexity manages all onboarding — messaging coordination, reference verification, background checks, and drug screening.' },
                { icon: Clock, title: 'Timesheet Approvals', desc: 'Full timesheet submission and approval management — visibility and control over hours worked per engagement.' },
                { icon: FileText, title: 'Document Management', desc: 'Securely share onboarding materials, policies, and position-specific resources with your consultants.' },
                { icon: Headphones, title: 'Priority Support', desc: 'Direct access to the Perplexity team for fast, dedicated assistance on any request.' },
                { icon: UserPlus, title: 'Unlimited Team Seats', desc: 'Invite your full hiring team — no seat cap on the Free plan.' },
              ].map(({ icon: Icon, title, desc }) => (
                <div key={title} className="flex items-start gap-3">
                  <div className="p-1.5 rounded-md bg-white/5 flex-shrink-0 mt-0.5">
                    <Icon className="w-4 h-4 text-gray-300" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm mb-0.5">{title}</h4>
                    <p className="text-gray-400 text-xs leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
              {/* Pro-only callout */}
              <div className="pt-3 border-t border-white/10 space-y-2">
                <p className="text-xs font-semibold tracking-widest text-gray-500 uppercase">Pro Plan Only</p>
                {[
                  'Candidate Pipeline & Tracking',
                  'Direct Candidate Messaging',
                  'Pro Job Postings (company name revealed)',
                  'Self-Service Dashboard Controls',
                ].map(item => (
                  <div key={item} className="flex items-center gap-2 opacity-50">
                    <Lock className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                    <span className="text-gray-500 text-xs">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="mt-auto">
              <button
                onClick={() => navigate('/customer-signup')}
                className="w-full bg-white/5 border border-white/20 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/10 hover:border-primary/40 transition-all transform hover:scale-105 flex items-center justify-center gap-3"
              >
                Sign Up for Free
                <Rocket className="w-5 h-5" />
              </button>
              <p className="text-center text-gray-500 text-xs mt-3">
                No credit card required. Start immediately.
              </p>
            </div>
          </div>
        </div>

        {/* Full Comparison Table */}
        <div className="card-gradient rounded-2xl border border-white/10 overflow-hidden mb-14">
          <div className="px-8 py-6 border-b border-white/10">
            <h2 className="text-2xl font-bold text-white">Full Feature Comparison</h2>
            <p className="text-gray-400 text-sm mt-1">See exactly what is included in each plan.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left px-8 py-4 text-gray-400 text-sm font-semibold w-1/2">Feature</th>
                  <th className="text-center px-6 py-4 text-sm font-semibold w-1/4">
                    <span className="block text-white font-bold text-base mb-0.5">Free</span>
                    <span className="text-gray-500 text-xs font-normal">$0 / mo</span>
                  </th>
                  <th className="text-center px-6 py-4 text-sm font-semibold w-1/4">
                    <span className="block text-primary font-bold text-base mb-0.5">Pro</span>
                    <span className="text-gray-400 text-xs font-normal">$499.99 / mo</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {FEATURE_MATRIX.map((feature, i) => {
                  const Icon = feature.icon;
                  return (
                    <tr key={feature.title} className={`border-b border-white/5 ${i % 2 === 0 ? 'bg-white/[0.02]' : ''}`}>
                      <td className="px-8 py-4">
                        <div className="flex items-start gap-3">
                          <div className="p-1.5 rounded-md bg-white/5 flex-shrink-0 mt-0.5">
                            <Icon className="w-4 h-4 text-gray-400" />
                          </div>
                          <div>
                            <p className="text-white text-sm font-medium">{feature.title}</p>
                            <p className="text-gray-500 text-xs mt-0.5 leading-relaxed">{feature.desc}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center align-top">
                        {feature.free ? (
                          <div className="flex flex-col items-center gap-1">
                            <CheckCircle className="w-5 h-5 text-green-400" />
                            {feature.freeNote && (
                              <span className="text-gray-400 text-xs leading-tight max-w-[120px] text-center">{feature.freeNote}</span>
                            )}
                          </div>
                        ) : (
                          <XCircle className="w-5 h-5 text-gray-600 mx-auto" />
                        )}
                      </td>
                      <td className="px-6 py-4 text-center align-top">
                        {feature.pro ? (
                          <div className="flex flex-col items-center gap-1">
                            <CheckCircle className="w-5 h-5 text-primary" />
                            {feature.proNote && (
                              <span className="text-gray-400 text-xs leading-tight max-w-[120px] text-center">{feature.proNote}</span>
                            )}
                          </div>
                        ) : (
                          <XCircle className="w-5 h-5 text-gray-600 mx-auto" />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Comparison note */}
        <div className="text-center">
          <div className="inline-block card-gradient rounded-xl px-8 py-5 border border-primary/20">
            <p className="text-gray-300 text-sm">
              Not sure which plan is right for you?{' '}
              <button
                onClick={() => navigate('/workday-services')}
                className="text-primary font-semibold hover:underline"
              >
                Learn more about our services
              </button>{' '}
              or{' '}
              <button
                onClick={() => navigate('/requestaccess')}
                className="text-primary font-semibold hover:underline"
              >
                contact our team
              </button>
              .
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
